import SimpleRadioButtons, { FancyRadioButtonOption } from '~/components/shared/SimpleRadioButtons';
import * as React from 'react';
import { Button, FormGroup, Input } from 'reactstrap';
import { FilterString } from '..';
import { NotNullValue, NullValue, NullValueLabel } from '../providers/Constants';
import { StringArrayFilterEditProps } from '../providers/MultipleValue';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';
import { FiltersListButtonGroup, FiltersListCloseButton, FiltersListAddButton, FiltersListValueButton } from './index';
import { toKebabCase } from '~/util/StringUtil';

interface MultipleTextFilterEditState {
	currentValue: string;
	displayValidationError: boolean;
	selectedValueToggle: string;
}

enum ValueToggleType {
	Specific = 'specific',
	Any = 'any',
}

const valueToggleOptions: FancyRadioButtonOption[] = [
	{ name: ValueToggleType.Specific, label: 'Specific Values', disabled: false },
	{ name: ValueToggleType.Any, label: 'Any Value', disabled: false },
];

/**
 * Configures multiple simple text values within a filter
 */
class MultipleTextFilterEdit extends React.Component<StringArrayFilterEditProps, MultipleTextFilterEditState> {
	constructor(props) {
		super(props);

		this.state = {
			currentValue: '',
			displayValidationError: false,
			selectedValueToggle:
				Array.isArray(props.value) && props.value.includes(NotNullValue)
					? ValueToggleType.Any
					: ValueToggleType.Specific,
		};
	}

	/**
	 * Update local state when the controlled text box is updated
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ currentValue: e.currentTarget.value });
	};

	/**
	 * Returns a filtered list of values. Removes null values for non-nullable filters.
	 *
	 * @private
	 * @returns {string[]}
	 * @memberof MultipleTextFilterEdit
	 */
	private getValuesFromProps(): string[] {
		if (!this.props.value) {
			return [];
		}

		return this.props.value.filter((value) => this.props.filter.nullable || !!value);
	}

	/**
	 * Adds the value currently in state to the list of values selected.
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private addCurrentValue = () => {
		this.setState({
			displayValidationError: false,
		});

		if (!this.state.currentValue.trim()) {
			this.setState({
				displayValidationError: true,
			});

			return;
		}

		this.addValue(this.state.currentValue);
		this.setState({ currentValue: '' });
	};

	/**
	 * Begins filtering for any value
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private filterByAnyValue = () => {
		this.setState({
			displayValidationError: false,
		});

		this.props.onChange([NotNullValue], false);
	};

	private addValue = (value: string) => {
		const trimmedValue = value.trim();
		const values = this.getValuesFromProps();

		// ensure uniqueness
		if (trimmedValue && !values.includes(trimmedValue)) {
			// let the parent component know what the new values are.
			this.props.onChange(
				[...values, trimmedValue].filter((val) => val !== NotNullValue),
				false,
			);
		}
	};

	/**
	 * Allow users to hit enter to add new values.
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.addCurrentValue();
		}
	};

	/**
	 * Remove a specific value from the list
	 *
	 * @private
	 * @memberof MultipleTextFieldEdit
	 */
	private removeValue = (fieldIndex: number) => {
		const values = this.getValuesFromProps();

		// let the parent component know what the new values are.
		const newValues = values.filter((_, index) => index !== fieldIndex);
		this.props.onChange(newValues, false);
	};

	/**
	 * Update state when the value toggle changes
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private handleValueToggleChange = (selectedName: string) => {
		this.setState({
			selectedValueToggle: selectedName,
		});
	};

	/**
	 * Handles the click after adding a filter for "no value"
	 *
	 * @private
	 * @memberof MultipleTextFilterEdit
	 */
	private handleAddNoValueFilter = () => {
		this.setState({
			displayValidationError: false,
		});

		this.addValue(NullValue);
	};

	public render() {
		const values = this.getValuesFromProps();
		const filterActionClass =
			'filters-popover__actions ' + (values.length > 0 ? 'filters-popover__actions--divider' : '');

		const {
			filter: { nullable, options, name },
		} = this.props;

		const { selectedValueToggle } = this.state;

		const isDisabled = values.includes(NullValue);

		return (
			<FormGroup className="filters-popover__form-group">
				{nullable ? (
					<>
						<SimpleRadioButtons
							name="valueToggle"
							value={selectedValueToggle}
							onChange={this.handleValueToggleChange}
							options={valueToggleOptions}
						/>
					</>
				) : null}

				{selectedValueToggle === ValueToggleType.Specific ? (
					<>
						<hr />
						<Input
							id="fieldValue"
							type="text"
							autoFocus
							placeholder="Enter a value to filter by"
							value={this.state.currentValue}
							onChange={this.handleOnChange}
							onKeyDown={this.handleOnKeyDown}
							autoComplete="off"
							data-testid={`input-to-filter-by-${toKebabCase(name)}`}
						/>

						{nullable ? (
							<>
								<Button
									className={
										isDisabled
											? 'fitlers-popover__btn-link fitlers-popover__btn-link--disabled'
											: 'fitlers-popover__btn-link'
									}
									type="button"
									color="secondary"
									onClick={this.handleAddNoValueFilter}
									disabled={isDisabled}
								>
									+ Add {options && options.nullValueLabel ? options.nullValueLabel : NullValueLabel}{' '}
									Filter {isDisabled ? <span>(Disabled)</span> : null}
								</Button>{' '}
							</>
						) : null}

						<div className={filterActionClass}>
							<FiltersListAddButton onClick={this.addCurrentValue} />
						</div>

						{values
							.filter((value) => value !== NotNullValue)
							.map((value, index) => {
								return (
									<React.Fragment key={index}>
										<FiltersListButtonGroup>
											<FiltersListValueButton>
												{FilterString(value, options)}
											</FiltersListValueButton>
											<FiltersListCloseButton
												ariaLabel={`Remove ${value} as a filter`}
												onClick={() => {
													this.removeValue(index);
												}}
											/>
										</FiltersListButtonGroup>{' '}
									</React.Fragment>
								);
							})}
						{this.state.displayValidationError ? <FilterEditRequiredValidation /> : null}
					</>
				) : (
					<div className={filterActionClass}>
						<FiltersListAddButton onClick={this.filterByAnyValue} />
					</div>
				)}
			</FormGroup>
		);
	}
}

export default MultipleTextFilterEdit;
